import type {
    FocusEventHandler,
    KeyboardEventHandler,
    ReactNode,
    Ref,
} from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
import classNames from "classnames";
import { ArrowDownIcon, CloseIcon } from "@icons";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { InputSlot } from "@design-system/Input/InputParts";
import inputStyles from "@design-system/Input/Input.module.scss";
import {
    usePickerQuery,
    usePickerRoot,
    type PickerRootContextValue,
} from "./PickerContext";
import { composeHandlers } from "@design-system/overlay/composeHandlers";
import { usePickerCellEditorKeyDown } from "./usePickerCellEditorKeys";
import { usePickerLevelKeyboard } from "./usePickerLevelKeyboard";
import { usePickerTypedCommitHandlers } from "./usePickerTypedCommit";
import styles from "./PickerInputTrigger.module.scss";

/** The active root, which must be a Combobox-kind root (text trigger) for `Combobox.Input` to bind. */
export const useInputTriggerRoot = (
    partName: string,
): PickerRootContextValue => {
    const root = usePickerRoot();

    if (root.kind !== "combobox") {
        throw new Error(`${partName} needs a text-trigger Picker.Select root`);
    }

    return root;
};

/**
 * Multi-select summary ahead of the caret. The outer span is the flex item
 * that yields width; a string ellipsizes with a tooltip inside it.
 */
export const PickerInputTriggerSummary = ({
    summary,
}: {
    summary: ReactNode;
}) => (
    <span className={styles.inputTriggerSummary} data-picker-part="summary">
        {typeof summary === "string" ? (
            <BreakableText
                text={summary}
                showTooltip
                className={styles.inputTriggerSummaryText}
            />
        ) : (
            summary
        )}
    </span>
);

export interface PickerInputTriggerBodyProps {
    placeholder?: string;
    showChevron: boolean;
    clearable: boolean;
    summary?: ReactNode;
    controlClassName?: string;
    chevronClassName?: string;
    ref?: Ref<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
}

/**
 * What every input-as-trigger shell shares: the optional multi-select
 * summary, Base UI's `Combobox.Input`, a clear button whose slot is always
 * reserved so the chevron never moves, and the chevron that is the Base UI
 * trigger.
 */
export const PickerInputTriggerBody = ({
    placeholder,
    showChevron,
    clearable,
    summary,
    controlClassName,
    chevronClassName,
    ref,
    onKeyDown,
    onBlur,
}: PickerInputTriggerBodyProps) => {
    const root = useInputTriggerRoot("Picker.InputTrigger");
    const { inputValue, isPopupHidden } = usePickerQuery();
    const handleLevelKeyDown = usePickerLevelKeyboard();
    const typedCommit = usePickerTypedCommitHandlers();
    const handleCellEditorKeyDown = usePickerCellEditorKeyDown();
    const resolvedSummary =
        summary ?? (root.isMultiple ? root.selectionLabel : null);
    const hasSummary =
        resolvedSummary !== null && resolvedSummary !== undefined;
    const hasClearTarget =
        !root.isDisabled &&
        (inputValue !== "" || root.selectedValues.length > 0);

    return (
        <>
            {hasSummary && (
                <PickerInputTriggerSummary summary={resolvedSummary} />
            )}
            <ComboboxBase.Input
                ref={ref}
                className={classNames(
                    inputStyles.control,
                    styles.inputTriggerControl,
                    controlClassName,
                )}
                placeholder={hasSummary ? undefined : placeholder}
                aria-invalid={root.isInvalid || undefined}
                onKeyDown={composeHandlers(
                    composeHandlers(
                        composeHandlers(
                            handleLevelKeyDown,
                            typedCommit.onKeyDown,
                        ),
                        handleCellEditorKeyDown,
                    ),
                    onKeyDown,
                )}
                onBlur={composeHandlers(
                    composeHandlers(typedCommit.onBlur, root.onTriggerBlur),
                    onBlur,
                )}
            />
            {clearable && (
                <InputSlot side="right">
                    <ComboboxBase.Clear
                        keepMounted
                        className={classNames(inputStyles.clearButton, {
                            [styles.inputTriggerClearHidden]: !hasClearTarget,
                        })}
                        aria-label="Clear"
                        aria-hidden={!hasClearTarget || undefined}
                        tabIndex={-1}
                        data-slot-box
                    >
                        <CloseIcon aria-hidden />
                    </ComboboxBase.Clear>
                </InputSlot>
            )}
            {showChevron && (
                <InputSlot side="right">
                    <ComboboxBase.Trigger
                        className={classNames(styles.inputTriggerChevron, {
                            [styles.inputTriggerChevronHidden]: isPopupHidden,
                        })}
                        aria-label="Toggle options"
                        aria-hidden={isPopupHidden || undefined}
                        tabIndex={-1}
                        data-picker-chevron=""
                        data-hidden={isPopupHidden ? "" : undefined}
                    >
                        <ArrowDownIcon
                            className={chevronClassName}
                            aria-hidden
                        />
                    </ComboboxBase.Trigger>
                </InputSlot>
            )}
        </>
    );
};
